import { useEffect } from 'react';
import { PageHeader } from '@/components/organisms/PageHeader';
import { logPageView } from '@/services/analytics.service';

export const GuidelinesPage = () => {

  useEffect(() => {
    logPageView('Community Guidelines Page');
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHeader title="Community Guidelines" showBackButton />

      {/* Content */}
      <main className="max-w-[800px] mx-auto px-4 py-12">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <div className="mb-8">
            <p className="text-muted-foreground text-sm">
              Last updated: {new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Welcome to tuval.space</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              tuval.space is a collaborative pixel art platform where creativity
              and community come together. These guidelines help ensure a
              positive experience for everyone. By using our platform, you agree
              to follow these guidelines.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Be Respectful</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Treat all members of the community with respect and kindness. We
              welcome diverse perspectives and encourage constructive
              collaboration.
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>No harassment, bullying, or hate speech</li>
              <li>No personal attacks or threats</li>
              <li>Respect others' creative work and contributions</li>
              <li>Be patient with newcomers and help them learn</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. Content Guidelines</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When creating content on tuval.space, please ensure it is:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Appropriate for all ages</li>
              <li>Not offensive, harmful, or illegal</li>
              <li>Not spam or repetitive content</li>
              <li>Respectful of intellectual property rights</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Prohibited content includes but is not limited to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Explicit or adult content</li>
              <li>Violence, gore, or disturbing imagery</li>
              <li>Hate symbols or discriminatory content</li>
              <li>Copyrighted material without permission</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. Pixel Placement Etiquette</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When placing pixels on shared boards:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Respect existing artwork and collaborations</li>
              <li>Don't intentionally vandalize or destroy others' work</li>
              <li>Use your daily quota thoughtfully</li>
              <li>Consider the overall composition of the board</li>
              <li>Communicate with other artists when working on shared projects</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. Fair Use of Resources</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our platform operates on a quota system to ensure fair access for
              all users:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Don't attempt to circumvent or exploit the quota system</li>
              <li>Don't use automated tools or scripts to place pixels</li>
              <li>Don't create multiple accounts to gain unfair advantages</li>
              <li>Respect the daily pixel limits and accumulation rules</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Board Management</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you create a board:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>You are responsible for moderating content on your boards</li>
              <li>Set clear expectations for collaborators</li>
              <li>Respect the privacy settings of your boards</li>
              <li>Don't use boards for spam or advertising</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Reporting Issues</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you encounter content or behavior that violates these guidelines:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Report inappropriate content through the platform</li>
              <li>Contact moderators or administrators</li>
              <li>Provide clear details about the violation</li>
              <li>Don't engage in vigilante moderation or harassment</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Account Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Protect your account and the community:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Use a strong, unique password</li>
              <li>Don't share your account credentials</li>
              <li>Report suspicious activity immediately</li>
              <li>Keep your account information up to date</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Collaboration and Teamwork</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              tuval.space thrives on collaboration:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Work together on shared projects</li>
              <li>Give credit to collaborators when appropriate</li>
              <li>Be open to feedback and suggestions</li>
              <li>Celebrate the community's collective creativity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Consequences of Violations</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Violations of these guidelines may result in:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2 mb-4">
              <li>Warnings and educational messages</li>
              <li>Temporary restrictions on pixel placement</li>
              <li>Removal of inappropriate content</li>
              <li>Temporary or permanent account suspension</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We reserve the right to take appropriate action based on the
              severity and frequency of violations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. Changes to Guidelines</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              These guidelines may be updated periodically to reflect changes in
              our community standards and platform features. We encourage you to
              review them regularly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. Questions or Concerns</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If you have questions about these guidelines or need clarification,
              please reach out through our platform or visit our GitHub repository.
              We're here to help maintain a positive and creative community.
            </p>
          </section>

          <div className="mt-12 p-6 bg-muted/50 rounded-lg border border-border">
            <p className="text-foreground font-semibold mb-2">
              Remember: tuval.space is a shared creative space. Let's work
              together to make it welcoming, inspiring, and fun for everyone!
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

